/**
 * Account detail handlers: payload builders + role API transform
 */
import type { UserDetail } from 'modules/account/services/useGetAccountInfo';
import type {
  GovAgencyItems,
  ReplaceUserRoles,
  RoleAssignmentItem,
  RolesAssignment,
  RolesToAssign,
} from 'modules/account/types';

// --- Payload builders ---

export interface AccountUpdatePayload {
  userId: string;
  userActiveStatus: boolean;
  newUserInfo: {
    title: string;
    name: string;
    email: string;
    expiredAt: string | null;
    miscData: {
      Phone: string;
      JobPosition?: string;
      Address?: string;
      IdDoc?: string;
      IdDocIssuedAt?: string;
      IdDocIssuedBy?: string;
      Dob?: string;
      Gender?: string;
    };
  };
}

export interface RolesAssignmentPayload {
  forClientBusiness: RolesToAssign['rolesAssignment']['forClientBusiness'];
  forGovAgencyBusiness: RolesToAssign['rolesAssignment']['forGovAgencyBusiness'];
  forAdmin: RolesToAssign['rolesAssignment']['forAdminRoles'];
  forSystem: RolesToAssign['rolesAssignment']['forSystemRoles'];
}

function getExpiredAt(userInfo: UserDetail): string | null {
  if (userInfo['noExpired'] || !userInfo.userAuthen?.expiredAt) return null;
  return userInfo.userAuthen.expiredAt;
}

export function buildAccountUpdatePayload(userInfo: UserDetail): AccountUpdatePayload {
  return {
    userId: userInfo.id,
    userActiveStatus: Boolean(userInfo.status),
    newUserInfo: {
      title: userInfo.title,
      name: userInfo.name,
      email: `${userInfo.email}`,
      expiredAt: getExpiredAt(userInfo),
      miscData: userInfo.miscData as AccountUpdatePayload['newUserInfo']['miscData'],
    },
  };
}

export function buildRolesAssignmentPayload(rolesToAssign: RolesToAssign): RolesAssignmentPayload {
  const { rolesAssignment } = rolesToAssign;
  return {
    forClientBusiness: rolesAssignment.forClientBusiness,
    forGovAgencyBusiness: rolesAssignment.forGovAgencyBusiness,
    forAdmin: rolesAssignment.forAdminRoles,
    forSystem: rolesAssignment.forSystemRoles,
  };
}

export function allPromisesSettledSuccessfully(results: PromiseSettledResult<unknown>[]): boolean {
  return results.every(
    (r) => r.status === 'fulfilled' && (r.value as { message?: string } | null)?.message
  );
}

// --- Role API transform ---

function normalizeApiResponse(
  api: ReplaceUserRoles | null | undefined
): Record<string, unknown> | null {
  if (!api) return null;
  const record = api as Record<string, unknown>;
  const unwrapped = record.data as Record<string, unknown> | undefined;
  if (unwrapped && (unwrapped.adminRoles !== undefined || unwrapped.systemRoles !== undefined)) {
    return unwrapped;
  }
  return record;
}

function getAdminRoleItems(api: Record<string, unknown>): Array<{ roleId: string; orgId: string }> {
  const adminRoles = api.adminRoles as
    | Array<{ adminRoleId?: string; adminRole?: { id?: string }; targetOrgId?: string }>
    | undefined;
  if (adminRoles?.length) {
    return adminRoles
      .map((r) => ({
        roleId: r.adminRoleId ?? r.adminRole?.id ?? '',
        orgId: r.targetOrgId ?? '',
      }))
      .filter((item) => item.roleId);
  }
  const forAdmin = api.forAdmin as RoleAssignmentItem[] | undefined;
  if (forAdmin?.length) return forAdmin;
  return [];
}

function getSystemRoleItems(
  api: Record<string, unknown>
): Array<{ roleId: string; orgId: string }> {
  const systemRoles = api.systemRoles as
    | Array<{ systemRoleId?: string; systemRole?: { id?: string }; systemOrgId?: string }>
    | undefined;
  if (systemRoles?.length) {
    return systemRoles
      .map((r) => ({
        roleId: r.systemRoleId ?? r.systemRole?.id ?? '',
        orgId: r.systemOrgId ?? '',
      }))
      .filter((item) => item.roleId);
  }
  const forSystem = api.forSystem as RoleAssignmentItem[] | undefined;
  if (forSystem?.length) return forSystem;
  return [];
}

export function apiRolesToRolesAssignment(
  api: ReplaceUserRoles | null | undefined
): RolesAssignment | null {
  const apiRecord = normalizeApiResponse(api);
  if (!apiRecord) return null;
  const apiTyped = apiRecord as ReplaceUserRoles;
  return {
    forClientBusiness: (apiTyped.clientBusinessRoles ?? []).map((r) => ({
      roleId: r.clientBusinessRoleId,
      orgId: r.targetOrgId,
    })),
    forGovAgencyBusiness: (apiTyped.govAgencyBusinessRoles ?? []).map((r) => ({
      roleId: r.govAgencyBusinessRoleId,
      orgId: r.targetOrgId,
      includedClientIds: r.includedClientIds ?? null,
      includedClientByBoundary: r.includedClientByBoundary ?? false,
    })) as GovAgencyItems,
    forAdminRoles: getAdminRoleItems(apiRecord),
    forSystemRoles: getSystemRoleItems(apiRecord),
  };
}

export function isRoleAssigned(
  api: ReplaceUserRoles | null | undefined,
  roleType: string,
  roleId: string,
  isCQQL?: boolean
): boolean {
  const apiRecord = normalizeApiResponse(api);
  if (!apiRecord) return false;
  const apiTyped = apiRecord as ReplaceUserRoles;
  const normalizeId = (id: string | undefined) => (id ? String(id) : '');
  const roleIdNorm = normalizeId(roleId);

  if (roleType === 'systemRoles') {
    const systemItems = (apiTyped.systemRoles ?? []) as Array<{
      systemRoleId?: string;
      systemRole?: { id?: string };
    }>;
    if (
      systemItems.some(
        (r) =>
          normalizeId(r.systemRoleId) === roleIdNorm || normalizeId(r.systemRole?.id) === roleIdNorm
      )
    )
      return true;
    const forSystem = apiRecord.forSystem as Array<{ roleId?: string }> | undefined;
    return !!forSystem?.some((r) => normalizeId(r.roleId) === roleIdNorm);
  }
  if (roleType === 'adminRoles') {
    const adminItems = (apiTyped.adminRoles ?? []) as Array<{
      adminRoleId?: string;
      adminRole?: { id?: string };
    }>;
    if (
      adminItems.some(
        (r) =>
          normalizeId(r.adminRoleId) === roleIdNorm || normalizeId(r.adminRole?.id) === roleIdNorm
      )
    )
      return true;
    const forAdmin = apiRecord.forAdmin as Array<{ roleId?: string }> | undefined;
    return !!forAdmin?.some((r) => normalizeId(r.roleId) === roleIdNorm);
  }
  if (roleType === 'businessRoles') {
    if (isCQQL) {
      return (apiTyped.govAgencyBusinessRoles ?? []).some(
        (r) =>
          normalizeId(r.govAgencyBusinessRoleId) === normalizeId(roleId) ||
          normalizeId(r.govAgencyBusinessRole?.id) === normalizeId(roleId)
      );
    }
    return (apiTyped.clientBusinessRoles ?? []).some(
      (r) =>
        normalizeId(r.clientBusinessRoleId) === normalizeId(roleId) ||
        normalizeId(r.clientBusinessRole?.id) === normalizeId(roleId)
    );
  }
  return false;
}
